// Mapa de códigos de país (DDI) mais comuns
const countryCodesMap = {
	'+1': { iso: 'US', length: 2 },      // Estados Unidos/Canadá
	'+44': { iso: 'GB', length: 3 },     // Reino Unido
	'+49': { iso: 'DE', length: 3 },     // Alemanha
	'+33': { iso: 'FR', length: 3 },     // França
	'+39': { iso: 'IT', length: 3 },     // Itália
	'+34': { iso: 'ES', length: 3 },     // Espanha
	'+351': { iso: 'PT', length: 4 },    // Portugal
	'+54': { iso: 'AR', length: 3 },     // Argentina
	'+55': { iso: 'BR', length: 3 },     // Brasil
	'+56': { iso: 'CL', length: 3 },     // Chile
	'+57': { iso: 'CO', length: 3 },     // Colômbia
	'+52': { iso: 'MX', length: 3 },     // México
	'+51': { iso: 'PE', length: 3 },     // Peru
	'+598': { iso: 'UY', length: 4 },    // Uruguai
	'+58': { iso: 'VE', length: 3 },     // Venezuela
	'+593': { iso: 'EC', length: 4 },    // Equador
	'+595': { iso: 'PY', length: 4 },    // Paraguai
	'+591': { iso: 'BO', length: 4 },    // Bolívia
	'+86': { iso: 'CN', length: 3 },     // China
	'+81': { iso: 'JP', length: 3 },     // Japão
	'+82': { iso: 'KR', length: 3 },     // Coreia do Sul
	'+91': { iso: 'IN', length: 3 },     // Índia
	'+61': { iso: 'AU', length: 3 },     // Austrália
	'+64': { iso: 'NZ', length: 3 },     // Nova Zelândia
	'+27': { iso: 'ZA', length: 3 },     // África do Sul
	'+20': { iso: 'EG', length: 3 },     // Egito
	'+971': { iso: 'AE', length: 4 },    // Emirados Árabes
	'+966': { iso: 'SA', length: 4 },    // Arábia Saudita
	'+7': { iso: 'RU', length: 2 },      // Rússia
	'+380': { iso: 'UA', length: 4 },    // Ucrânia
	'+48': { iso: 'PL', length: 3 },     // Polônia
	'+90': { iso: 'TR', length: 3 },     // Turquia
};

/**
 * Detecta o país pelo código DDI do número de telefone
 * @param {string} phoneNumber - Número de telefone com ou sem código de país
 * @returns {string} - Código ISO do país (ex: 'BR', 'US', 'PT')
 */
export const detectCountryFromNumber = (phoneNumber) => {
	if (!phoneNumber || phoneNumber.length < 2) return "BR";

	// Remover espaços e caracteres especiais para análise
	const cleanNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');

	// Verificar se começa com +
	if (cleanNumber.startsWith('+')) {
		// Tentar códigos de 3 dígitos primeiro
		for (let i = 4; i >= 2; i--) {
			const code = cleanNumber.substring(0, i);
			if (countryCodesMap[code]) {
				return countryCodesMap[code].iso;
			}
		}
	} else if (/^\d/.test(cleanNumber)) {
		// Se não começa com +, tentar identificar pelo número
		// Códigos de 3 dígitos
		if (cleanNumber.startsWith('598') || cleanNumber.startsWith('593') ||
		    cleanNumber.startsWith('595') || cleanNumber.startsWith('591') ||
		    cleanNumber.startsWith('971') || cleanNumber.startsWith('966') ||
		    cleanNumber.startsWith('380') || cleanNumber.startsWith('351')) {
			const code = '+' + cleanNumber.substring(0, 3);
			return countryCodesMap[code]?.iso || 'BR';
		}
		// Códigos de 2 dígitos
		const code = '+' + cleanNumber.substring(0, 2);
		if (countryCodesMap[code]) {
			return countryCodesMap[code].iso;
		}
	}

	return "BR"; // Padrão Brasil
};

/**
 * Remove o código DDI do número de telefone para exibição
 * @param {string} phoneNumber - Número de telefone com código de país
 * @returns {string} - Número sem o código de país
 */
export const removeCountryCode = (phoneNumber) => {
	if (!phoneNumber) return "";

	// Remover espaços e caracteres especiais para análise
	const cleanNumber = phoneNumber.replace(/[\s\-\(\)]/g, '');

	// Se começa com +
	if (cleanNumber.startsWith('+')) {
		// Tentar códigos de 4 dígitos primeiro, depois 3, depois 2
		for (let i = 4; i >= 2; i--) {
			const code = cleanNumber.substring(0, i);
			if (countryCodesMap[code]) {
				// Remover o código do país
				return cleanNumber.substring(i);
			}
		}
	}
	// Se começa apenas com números (sem +)
	else if (/^\d/.test(cleanNumber)) {
		// Códigos de 3 dígitos
		if (cleanNumber.startsWith('598') || cleanNumber.startsWith('593') ||
		    cleanNumber.startsWith('595') || cleanNumber.startsWith('591') ||
		    cleanNumber.startsWith('971') || cleanNumber.startsWith('966') ||
		    cleanNumber.startsWith('380') || cleanNumber.startsWith('351')) {
			return cleanNumber.substring(3);
		}
		// Códigos de 2 dígitos (como 55 do Brasil)
		const code = '+' + cleanNumber.substring(0, 2);
		if (countryCodesMap[code]) {
			return cleanNumber.substring(2);
		}
	}

	return phoneNumber;
};

/**
 * Adiciona o código DDI ao número de telefone
 * @param {string} phoneNumber - Número de telefone sem código de país
 * @param {string} countryISO - Código ISO do país (ex: 'BR', 'US')
 * @returns {string} - Número com código de país
 */
export const addCountryCode = (phoneNumber, countryISO) => {
	if (!phoneNumber) return "";

	// Remover espaços e caracteres especiais
	const cleanNumber = phoneNumber.replace(/[\s\-\(\)]/g, '').replace(/^\+/, '');

	// Encontrar o código DDI pelo ISO
	const countryCode = Object.entries(countryCodesMap).find(
		([code, data]) => data.iso === countryISO
	);

	if (countryCode) {
		const ddi = countryCode[0]; // Ex: '+55'
		// Se o número já começa com o DDI, não adicionar novamente
		if (cleanNumber.startsWith(ddi.substring(1))) {
			return cleanNumber;
		}
		return ddi.substring(1) + cleanNumber; // Retorna sem o +
	}

	return cleanNumber;
};